import { PERMISSIONS } from '@ibe/constants';
import {
  Button,
  DataTable,
  type DataTableColumn,
  ErrorState,
  LoadingState,
  Pagination,
  SearchInput,
} from '@ibe/ui';
import { BedDouble, Building2, CalendarDays, DollarSign, Download, Eye } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { StatCard, StatCardSkeleton, STAT_TONE } from '../../../components/stat-card/stat-card.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { useApiList } from '../../../lib/api/hooks.js';
import { reservationRow, type ReservationRow } from '../../reservations/index.js';
import { reservationSummaryEndpoints } from '../api/endpoints.js';
import { type PropertyBreakdown, useReservationDashboard } from '../api/queries.js';
import { PropertyBreakdownChart } from '../components/property-breakdown-chart.js';

/**
 * The reservation summary dashboard.
 *
 * It uses a single aggregate endpoint for the totals and property breakdown,
 * and the paginated reservation list for the rows.
 */

const FIRST_PAGE = 1;
const LIST_PAGE_SIZE = 10;
const PERCENTAGE_MULTIPLIER = 100;

function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

function reservationListCsv(rows: readonly ReservationRow[]): string {
  const headers = [
    'Property',
    'Guest',
    'Reference',
    'Reserved',
    'Check-in',
    'LOS',
    'Check-out',
    'Total',
  ];
  const lines = rows.map((row) =>
    [
      row.propertyName,
      row.guestName,
      row.reference,
      row.createdAt,
      row.checkIn,
      row.nights,
      row.checkOut,
      row.totalFormatted,
    ]
      .map((cell) => `"${String(cell).replaceAll('"', '""')}"`)
      .join(','),
  );
  return [headers.join(','), ...lines].join('\n');
}

function propertyBreakdownCsv(rows: readonly PropertyBreakdown[]): string {
  const headers = ['Property', 'Reservations', 'Room nights', 'Total'];
  const lines = rows.map((row) =>
    [row.propertyName, row.reservationCount, row.roomNights, row.totalAmountFormatted]
      .map((cell) => `"${String(cell).replaceAll('"', '""')}"`)
      .join(','),
  );
  return [headers.join(','), ...lines].join('\n');
}

function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

const reservationSummaryColumns: readonly DataTableColumn<ReservationRow>[] = [
  {
    id: 'propertyName',
    header: 'Property',
    cell: (row) => <PrimaryCell label={row.propertyName} to={`/properties/${row.propertyId}`} />,
  },
  {
    id: 'guestName',
    header: 'Guest',
    cell: (row) => <PrimaryCell label={row.guestName} to={`/guests/${row.guestId}`} />,
  },
  {
    id: 'reference',
    header: 'Reference',
    cell: (row) => row.reference,
  },
  {
    id: 'createdAt',
    header: 'Reserved',
    cell: (row) => formatDateTime(row.createdAt),
  },
  {
    id: 'checkIn',
    header: 'Check-in',
    cell: (row) => formatDate(row.checkIn),
  },
  {
    id: 'nights',
    header: 'LOS',
    align: 'right',
    cell: (row) => String(row.nights),
  },
  {
    id: 'checkOut',
    header: 'Check-out',
    cell: (row) => formatDate(row.checkOut),
  },
  {
    id: 'totalFormatted',
    header: 'Total',
    align: 'right',
    cell: (row) => row.totalFormatted,
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    cell: (row) => (
      <RowActions
        actions={[{ label: `View ${row.reference}`, icon: Eye, to: `/reservations/${row.id}` }]}
      />
    ),
  },
];

const propertyBreakdownColumns: readonly DataTableColumn<PropertyBreakdown>[] = [
  {
    id: 'propertyName',
    header: 'Property name',
    cell: (row) => <PrimaryCell label={row.propertyName} to={`/properties/${row.propertyId}`} />,
  },
  {
    id: 'reservationCount',
    header: 'Reservations',
    align: 'right',
    cell: (row) => String(row.reservationCount),
  },
  {
    id: 'roomNights',
    header: 'Room nights',
    align: 'right',
    cell: (row) => String(row.roomNights),
  },
  {
    id: 'totalAmountFormatted',
    header: 'Total',
    align: 'right',
    cell: (row) => row.totalAmountFormatted,
  },
  {
    id: 'share',
    header: 'Share',
    align: 'right',
    cell: (row) => `${String(Math.round(row.share * PERCENTAGE_MULTIPLIER))}%`,
  },
];

export function ReservationSummaryPage(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const q = params.get('q') ?? '';
  const page = Number(params.get('page') ?? String(FIRST_PAGE));

  const dashboardQuery = useReservationDashboard({ from, to, q });
  const listQuery = useApiList({
    resource: 'reservations',
    path: reservationSummaryEndpoints.list,
    schema: reservationRow,
    params: {
      page,
      pageSize: LIST_PAGE_SIZE,
      ...(q === '' ? {} : { q }),
      sortBy: 'createdAt',
      sortDirection: 'desc',
      scope: {
        ...(from === '' ? {} : { from }),
        ...(to === '' ? {} : { to }),
      },
    },
  });

  const update = (changes: Readonly<Record<string, string | undefined>>): void => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined || value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  const handleExportReservations = (): void => {
    const items = listQuery.data?.items ?? [];
    downloadCsv(`reservations-${from || 'all'}-${to || 'all'}.csv`, reservationListCsv(items));
  };

  const handleExportBreakdown = (): void => {
    const breakdown = dashboardQuery.data?.breakdown ?? [];
    downloadCsv(
      `property-breakdown-${from || 'all'}-${to || 'all'}.csv`,
      propertyBreakdownCsv(breakdown),
    );
  };

  if (dashboardQuery.error !== null && dashboardQuery.data === undefined) {
    return (
      <PermissionGuard permission={PERMISSIONS.RESERVATION_DASHBOARD} fallback={<NotPermitted />}>
        <section className="space-y-section">
          <h1 className="text-page-title font-semibold">Reservation Summary</h1>
          <ErrorState
            description={
              errorTextFor(dashboardQuery.error, 'The reservation summary could not be loaded.') ??
              ''
            }
            onRetry={() => {
              void dashboardQuery.refetch();
            }}
          />
        </section>
      </PermissionGuard>
    );
  }

  return (
    <PermissionGuard permission={PERMISSIONS.RESERVATION_DASHBOARD} fallback={<NotPermitted />}>
      <section className="space-y-section">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-1">
            <h1 className="text-page-title font-semibold">Reservation Summary</h1>
            <p className="text-body text-muted-foreground">
              A filtered view of reservations, grouped by property and value.
            </p>
          </div>

          <div className="flex flex-wrap items-end gap-field">
            <div className="flex flex-col gap-1">
              <label htmlFor="reservation-summary-from" className="text-sm text-muted-foreground">
                Reserved on from
              </label>
              <input
                id="reservation-summary-from"
                type="date"
                className="h-10 rounded-md border border-input bg-surface px-3 text-sm"
                value={from}
                onChange={(event) => {
                  update({ from: event.target.value, page: '1' });
                }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="reservation-summary-to" className="text-sm text-muted-foreground">
                To
              </label>
              <input
                id="reservation-summary-to"
                type="date"
                className="h-10 rounded-md border border-input bg-surface px-3 text-sm"
                value={to}
                onChange={(event) => {
                  update({ to: event.target.value, page: '1' });
                }}
              />
            </div>
            <SearchInput
              className="w-full max-w-xs"
              label="Search by reference, guest or property"
              defaultValue={q}
              onSearch={(term) => {
                update({ q: term, page: '1' });
              }}
            />
          </div>
        </header>

        <dl className="grid gap-section sm:grid-cols-2 xl:grid-cols-4">
          {dashboardQuery.data === undefined ? (
            <>
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
            </>
          ) : (
            <>
              <StatCard
                label="Total reservations"
                value={String(dashboardQuery.data.totalReservations)}
                icon={CalendarDays}
                tone={STAT_TONE.NEUTRAL}
              />
              <StatCard
                label="Room nights"
                value={String(dashboardQuery.data.totalNights)}
                icon={BedDouble}
                tone={STAT_TONE.NEUTRAL}
              />
              <StatCard
                label="Total amount"
                value={dashboardQuery.data.totalAmountFormatted}
                icon={DollarSign}
                tone={STAT_TONE.NEUTRAL}
              />
              <StatCard
                label="Properties"
                value={String(dashboardQuery.data.breakdown.length)}
                icon={Building2}
                tone={STAT_TONE.NEUTRAL}
              />
            </>
          )}
        </dl>

        <div className="grid gap-section lg:grid-cols-2">
          <section className="rounded-lg border border-border bg-surface p-4 shadow-card">
            <h2 className="text-body font-semibold">Total value</h2>
            {dashboardQuery.isPending ? (
              <div className="flex h-64 items-center justify-center">
                <LoadingState label="Loading breakdown" />
              </div>
            ) : (
              <PropertyBreakdownChart data={dashboardQuery.data.breakdown} />
            )}
          </section>

          <section className="rounded-lg border border-border bg-surface p-4 shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-body font-semibold">Property breakdown</h2>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={dashboardQuery.data === undefined}
                onClick={handleExportBreakdown}
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                Export
              </Button>
            </div>
            <DataTable
              caption="Property breakdown"
              columns={propertyBreakdownColumns}
              rows={dashboardQuery.data?.breakdown ?? []}
              rowKey={(row) => row.propertyId}
              loading={dashboardQuery.isPending}
              error={errorTextFor(dashboardQuery.error)}
              onRetry={() => {
                void dashboardQuery.refetch();
              }}
              emptyTitle="No properties"
              emptyDescription="Try widening the dates or clearing the filters."
            />
          </section>
        </div>

        <section className="rounded-lg border border-border bg-surface p-4 shadow-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-body font-semibold">Reservations</h2>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={listQuery.data === undefined}
              onClick={handleExportReservations}
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Export
            </Button>
          </div>
          <DataTable
            caption="Reservations"
            columns={reservationSummaryColumns}
            rows={listQuery.data?.items ?? []}
            rowKey={(row) => row.id}
            loading={listQuery.isPending}
            error={errorTextFor(listQuery.error)}
            onRetry={() => {
              void listQuery.refetch();
            }}
            emptyTitle="No reservations"
            emptyDescription="Try widening the dates or clearing the filters."
          />
          {listQuery.data === undefined ? null : (
            <Pagination
              meta={listQuery.data.pagination}
              disabled={listQuery.isFetching}
              onPageChange={(next) => {
                update({ page: String(next) });
              }}
              itemNoun={{ one: 'reservation', many: 'reservations' }}
            />
          )}
        </section>
      </section>
    </PermissionGuard>
  );
}
